import React, { useState, useEffect } from 'react';
import { 
  Users, 
  CheckCircle2, 
  MapPin, 
  Calendar, 
  Send
} from 'lucide-react';
import { Team, Worker, BuildingBlock, Zone } from '../../types';

interface MorningCheckinProps {
  onCheckinSubmitted?: () => void;
}

export const MorningCheckin: React.FC<MorningCheckinProps> = ({ onCheckinSubmitted }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [blocks, setBlocks] = useState<BuildingBlock[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  
  const [selectedTeamId, setSelectedTeamId] = useState<string>('team-01');
  const [selectedBlockId, setSelectedBlockId] = useState<string>('block-a');
  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [foremanName, setForemanName] = useState<string>('Иванов И.И.');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [plannedSummary, setPlannedSummary] = useState<string>('Протяжка силового кабеля ВВГнг-LS 3х2.5 в стояках и монтаж лотков');
  
  const [selectedWorkerIds, setSelectedWorkerIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load master data
  useEffect(() => {
    fetch('/api/v1/hr/teams')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data.length > 0) {
          setTeams(data.data);
          setSelectedTeamId(data.data[0].id);
          setForemanName(data.data[0].foremanName);
        }
      });

    fetch('/api/v1/projects/proj-sz-2026/tree')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setBlocks(data.data.blocks);
          setZones(data.data.zones);
          if (data.data.zones.length > 0) {
            setSelectedZoneId(data.data.zones[0].id);
          }
        }
      });
  }, []);

  // Load workers for selected team
  useEffect(() => {
    if (!selectedTeamId) return;
    fetch(`/api/v1/hr/workers?teamId=${selectedTeamId}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setWorkers(data.data);
          // By default, select all active workers in this team
          setSelectedWorkerIds(data.data.filter((w: Worker) => w.status === 'active').map((w: Worker) => w.id));
        }
      });
  }, [selectedTeamId]);

  const toggleWorker = (id: string) => {
    setSelectedWorkerIds(prev =>
      prev.includes(id) ? prev.filter(wId => wId !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedWorkerIds(workers.map(w => w.id));
  };

  const clearAll = () => {
    setSelectedWorkerIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedWorkerIds.length === 0) {
      alert('Пожалуйста, выберите хотя бы одного рабочего на смену.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/foreman/checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          teamId: selectedTeamId,
          foremanName,
          blockId: selectedBlockId,
          assignedZoneId: selectedZoneId || null,
          plannedSummary,
          workerIds: selectedWorkerIds,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSuccessMessage(`✅ Утренний чек-ин успешно зафиксирован! Вышло: ${selectedWorkerIds.length} чел.`);
        if (onCheckinSubmitted) onCheckinSubmitted();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        alert(`Ошибка: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка при отправке чекина.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredZones = zones.filter(z => z.blockId === selectedBlockId);

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <span className="p-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl">
              <Users className="w-5 h-5" />
            </span>
            <span>Утренний чек-ин и расстановка звеньев</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Отметка явки электромонтажников и назначение на рабочие зоны (этажи/стояки/ГРЩ)
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 flex items-center gap-1 font-medium">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            Дата:
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-slate-50 border border-slate-200 text-slate-800 text-xs px-2.5 py-1.5 rounded-xl focus:outline-none focus:border-amber-500 font-semibold"
          />
        </div>
      </div>

      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm font-semibold flex items-center gap-3 animate-fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Team & Zone Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Team Select */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700">Бригада / Звено</label>
            <select
              value={selectedTeamId}
              onChange={(e) => {
                setSelectedTeamId(e.target.value);
                const t = teams.find(team => team.id === e.target.value);
                if (t) setForemanName(t.foremanName);
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500"
            >
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.foremanName})</option>
              ))}
            </select>
          </div>

          {/* Block Select */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700">Корпус / Блок</label>
            <select
              value={selectedBlockId}
              onChange={(e) => setSelectedBlockId(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500"
            >
              {blocks.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Zone Select */}
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-amber-600" />
              <span>Назначение на зону / захватку</span>
            </label>
            <select
              value={selectedZoneId}
              onChange={(e) => setSelectedZoneId(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500"
            >
              {filteredZones.map(z => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Step 2: Planned Summary */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-700">План работ на текущую смену</label>
          <input
            type="text"
            value={plannedSummary}
            onChange={(e) => setPlannedSummary(e.target.value)}
            placeholder="Например: Прокладка силового кабеля ВВГнг-LS 3х2.5, этажи 4-6"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:border-amber-500"
          />
        </div>

        {/* Step 3: Workers Check-in List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-900">
                Состав звена на объекте
              </span>
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                Вышло: {selectedWorkerIds.length} из {workers.length} чел.
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={selectAll}
                className="text-blue-700 hover:text-blue-800 font-medium px-2 py-1 bg-blue-50 rounded-lg border border-blue-200 transition"
              >
                Выбрать всех
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-slate-600 hover:text-slate-800 px-2 py-1 bg-slate-100 rounded-lg border border-slate-200 transition"
              >
                Снять выбор
              </button>
            </div>
          </div>

          {/* Workers Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-72 overflow-y-auto pr-1">
            {workers.map((w) => {
              const isChecked = selectedWorkerIds.includes(w.id);
              return (
                <div
                  key={w.id}
                  onClick={() => toggleWorker(w.id)}
                  className={`p-3 rounded-xl border cursor-pointer select-none transition flex items-center justify-between ${
                    isChecked
                      ? 'bg-amber-50/80 border-amber-300 shadow-sm'
                      : 'bg-slate-50/60 border-slate-200 opacity-70 hover:opacity-100 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}} // Handled by parent div
                      className="w-4 h-4 text-amber-600 rounded bg-white border-slate-300 focus:ring-0 pointer-events-none"
                    />
                    <div>
                      <div className="text-sm font-bold text-slate-900">{w.fullName}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <span className="bg-slate-200/80 px-1.5 py-0.5 rounded text-[10px] text-slate-700 font-semibold">
                          {w.rank} разряд
                        </span>
                        <span className="text-[10px] text-amber-700 font-bold flex items-center gap-0.5">
                          ⚡ Группа {w.ebGroup} ({w.ebVoltageLimit === 'above_1000V' ? '>1000В' : 'до 1000В'})
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right text-[11px] text-slate-500">
                    {isChecked ? (
                      <span className="text-emerald-700 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> На смене
                      </span>
                    ) : (
                      <span className="text-slate-400">Не вышел</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting || selectedWorkerIds.length === 0}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold py-3.5 rounded-xl shadow-md shadow-amber-500/20 active:scale-[0.99] transition flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            <span>Зафиксировать утренний выход ({selectedWorkerIds.length} чел.)</span>
          </button>
        </div>
      </form>
    </div>
  );
};
